import {DateTime, Duration} from 'luxon';


export const isLuxon = date => {

    return DateTime.isDateTime(date);
};


/**
 * @param dateTime {string|Date|DateTime}
 * @param defaultReturn {DateTime|null}
 * @return {DateTime|null}
 */
export const parse = (dateTime, defaultReturn = null) => {

    if (isLuxon(dateTime)) {

        return dateTime;
    }

    if (typeof dateTime === 'string') {

        return DateTime.fromISO(dateTime);
    }

    if (dateTime instanceof Date) {

        return DateTime.fromJSDate(dateTime);
    }

    return defaultReturn;
};


/**
 * @param jsDate {Date}
 * @return {string}
 */
export const jsDateToISODate = jsDate => {

    const monthZeroStart = jsDate.getMonth() + 1;
    const dayOneStart = jsDate.getDate();

    const isoDateParts = [
        jsDate.getFullYear(),
        monthZeroStart < 10 ? `0${monthZeroStart}` : monthZeroStart,
        dayOneStart < 10 ? `0${dayOneStart}` : dayOneStart
    ];

    return isoDateParts.join('-');
};


export const toLocalDateTime = (givenDateTime, timeZone = null) => {

    const dateTime = parse(givenDateTime);
    if (!timeZone) {

        return dateTime;
    }

    return dateTime.setZone(timeZone);
};


/**
 * @param date {String|DateTime}
 * @param seconds {Number}
 * @return {DateTime}
 */
export const addSeconds = (date, seconds) => {

    return parse(date)
        .plus({seconds});
};


/**
 * @param date {String|DateTime}
 * @param seconds {Number}
 * @return {DateTime}
 */
export const minusSeconds = (date, seconds) => {

    return parse(date)
        .minus({seconds});
};


/**
 * @param date {String|DateTime}
 * @param days {Number}
 * @return {DateTime}
 */
export const addDays = (date, days) => {

    return parse(date)
        .plus({days});
};


/**
 * @param date {String|DateTime}
 * @param days {Number}
 * @return {DateTime}
 */
export const minusDays = (date, days) => {

    return parse(date)
        .minus({days});
};


/**
 * @param date {String|DateTime}
 * @param months {Number}
 * @return {DateTime}
 */
export const addMonths = (date, months) => {

    return parse(date)
        .plus({months});
};


/**
 * @param date {String|DateTime}
 * @param months {Number}
 * @return {DateTime}
 */
export const minusMonths = (date, months) => {

    return parse(date)
        .minus({months});
};


/**
 * @param date {String|DateTime}
 * @param years {Number}
 * @return {DateTime}
 */
export const addYears = (date, years) => {

    return parse(date)
        .plus({years});
};


/**
 * @param date {String|DateTime}
 * @param years {Number}
 * @return {DateTime}
 */
export const minusYears = (date, years) => {

    return parse(date)
        .minus({years});
};


/**
 * @returns {DateTime}
 */
export const now = () => {

    return DateTime.local();
};


/**
 * @returns {DateTime}
 */
export const today = () => {

    return DateTime.local()
        .startOf('day');
};


/**
 * @param date1 {DateTime} Date to check
 * @param date2 {DateTime} Date to check
 * @return {Boolean} True of both dates are on the same day
 */
export const isSameDay = (date1, date2) => {

    return date1.hasSame(date2, 'day');
};


/**
 * @param date {DateTime} Date to check
 * @return {Boolean} True of given date is today
 */
export const isToday = date => {

    return isSameDay(date, today());
};


/**
 * @param dateToCheck
 * @param dateToCheckAgainst
 * @returns {Boolean}
 */
export const isSameMonthOrAfter = (dateToCheck, dateToCheckAgainst) => {

    const dateTimeMonthToCheck = parse(dateToCheck)
        .startOf('month');
    const dateTimeMonthToCheckAgainst = parse(dateToCheckAgainst)
        .startOf('month');

    return dateTimeMonthToCheck >= dateTimeMonthToCheckAgainst;
};


/**
 * @param dateToCheck
 * @param dateToCheckAgainst
 * @returns {Boolean}
 */
export const isSameMonthOrBefore = (dateToCheck, dateToCheckAgainst) => {

    const dateTimeMonthToCheck = parse(dateToCheck)
        .startOf('month');
    const dateTimeMonthToCheckAgainst = parse(dateToCheckAgainst)
        .startOf('month');

    return dateTimeMonthToCheck <= dateTimeMonthToCheckAgainst;
};


/**
 * @param base {any} Date(time) to check against
 * @param isAfterDate {any} Date(time) to check if after base
 * @return {boolean} True if after the base date(time)
 */
export const isAfter = (base, isAfterDate) => {

    return parse(base) <= parse(isAfterDate);
};


/**
 * @param base {any} Date(time) to check against
 * @param isBeforeDate {any} Date(time) to check if before base
 * @return {boolean} True if before base date(time)
 */
export const isBefore = (base, isBeforeDate) => {

    return parse(base) >= parse(isBeforeDate);
};


/**
 * @param dateToCheck {String}
 * @param startDate {String|null} Start date for the interval. Can be null.
 * @param endDate {String|null} End date of the interval. Can be null.
 * @return {Boolean} True if within start and end date; false if not
 */
export const isBetween = (dateToCheck, startDate, endDate) => {

    const dateTimeToCheck = parse(dateToCheck);
    if (!startDate || !endDate) {

        if (startDate && isBefore(startDate, dateTimeToCheck)) {

            return false;
        }

        return !endDate || isAfter(endDate, dateTimeToCheck);
    }

    return isAfter(startDate, dateTimeToCheck)
        && isBefore(endDate, dateTimeToCheck);
};


/**
 * @param dateToCheck {DateTime} Date to check
 * @return {Boolean} True if the given date is in the past; false if not
 */
export const isInPast = dateToCheck => {

    return dateToCheck <= now();
};


/**
 * @param durationData {Object} Duration data to format
 * @param format {String} Format to use - you can escape using 'single' quotes
 * @return {String}
 *
 * @see https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 */
export const toDuration = (durationData, format = 'hh mm') => {

    const duration = Duration.fromObject(durationData);

    return duration.toFormat(format);
};


/**
 * @param date {any} Any to parse
 * @return {string} ISO date
 */
export const toISODate = date => {

    return parse(date)
        .toISODate();
};


/**
 * @param date {any} Any to parse
 * @param format
 * @param options {object} Locale options
 * @return {string} Formatted date
 */
export const toDateFormat = (date, format = DateTime.DATE_MED, options = {}) => {

    return parse(date)
        .toLocaleString(format, options);
};
